import { getDateTimeByFormat } from "../../../../../redux/slices/dateTimeFormatSlice";
import { setDrawer } from "../../../../../redux/slices/drawerSlice";
import { RootState } from "../../../../../redux/store";
import { convertUtcToLocalString } from "../../../../../utils/functions/convert-to-local-timezone";
import {
  VIEWINGS_TABLE_COLUMNS,
  VIEWINGS_VIEW_MODE_QUERY_KEY,
} from "./constants";
import {
  AttendeeResponseStatus,
  FormattedScheduleDateTime,
  UserResponseFilterOption,
  ViewMode,
  ViewingStatus,
  ViewingsTableColumnConfig,
} from "./types";

export const isStatusKey = (key: string): key is ViewingStatus => {
  return (Object.values(ViewingStatus) as string[])?.includes(key);
};

export const getDisplayUrl = (url: string): string => {
  if (!url) return "";
  return url?.replace(/^https?:\/\//, "")?.replace(/\/$/, "");
};

export const formatRoomTypeLabel = (
  roomType: string | null | undefined
): string => {
  if (!roomType) return "";
  return roomType
    ?.split("_")
    ?.filter(Boolean)
    ?.map(
      (word) =>
        (word?.charAt(0)?.toUpperCase() ?? "") +
        (word?.slice(1)?.toLowerCase() ?? "")
    )
    ?.join(" ");
};

export const ensureExternalUrl = (url: string): string => {
  if (!url) return "";
  return /^https?:\/\//.test(url) ? url : `https://${url}`;
};

export const getFormattedScheduleDateTime = (
  scheduledTime: string | undefined
): FormattedScheduleDateTime => {
  const localScheduledTime = convertUtcToLocalString(
    scheduledTime ?? "",
    "dd MMM, yyyy, hh:mm a"
  );
  const browserTimezone = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone;
  return getDateTimeByFormat(
    new Date(localScheduledTime ?? ""),
    browserTimezone
  );
};

export const openCreateViewingDrawerAction = () =>
  setDrawer({ open: true, type: "create_viewing" });

export const isUserResponseOptionSelected = (
  option: UserResponseFilterOption,
  current: AttendeeResponseStatus[]
): boolean =>
  Boolean(option?.values?.every((value) => current?.includes(value)));

export const toggleUserResponseOption = (
  option: UserResponseFilterOption,
  current: AttendeeResponseStatus[]
): AttendeeResponseStatus[] => {
  if (isUserResponseOptionSelected(option, current)) {
    return current?.filter((value) => !option?.values?.includes(value)) ?? [];
  }
  return Array.from(new Set([...(current ?? []), ...(option?.values ?? [])]));
};

export const selectAuthClientCode = (state: RootState): string =>
  state?.auth?.user?.current_client_code ?? "";

export const findViewingsColumn = (id: string): ViewingsTableColumnConfig => {
  const column = VIEWINGS_TABLE_COLUMNS?.find((entry) => entry?.id === id);
  if (!column) {
    throw new Error(`Viewings table column "${id}" missing from config`);
  }
  return column;
};

export const lockedViewingsColumnSize = (
  id: string
): { size: number; minSize: number; maxSize: number } => {
  const width = findViewingsColumn(id)?.width;
  return { size: width, minSize: width, maxSize: width };
};

export const getViewModeFromSearch = (
  search: string,
  fallback: ViewMode = "grid"
): ViewMode => {
  const params = new URLSearchParams(search ?? "");
  if (params?.has(VIEWINGS_VIEW_MODE_QUERY_KEY?.list)) return "list";
  if (params?.has(VIEWINGS_VIEW_MODE_QUERY_KEY?.grid)) return "grid";
  return fallback;
};

